import { CurrencyAmount, JSBI, Token, Trade } from '@uniswap/sdk'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ArrowDown, Repeat } from 'react-feather'
import ReactGA from 'react-ga'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'
import AddressInputPanel from '../../components/AddressInputPanel'
import { ButtonError, ButtonLight, ButtonPrimary, ButtonConfirmed } from '../../components/Button'
import Card, { GreyCard } from '../../components/Card'
import { AutoColumn } from '../../components/Column'
import ConfirmSwapModal from '../../components/swap/ConfirmSwapModal'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import { SwapPoolTabs } from '../../components/NavigationTabs'
import { AutoRow, RowBetween } from '../../components/Row'
import AdvancedSwapDetailsDropdown from '../../components/swap/AdvancedSwapDetailsDropdown'
import BetterTradeLink from '../../components/swap/BetterTradeLink'
import confirmPriceImpactWithoutFee from '../../components/swap/confirmPriceImpactWithoutFee'
import { ArrowWrapper, BottomGrouping, SwapCallbackError, Wrapper } from '../../components/swap/styleds'
import TradePrice from '../../components/swap/TradePrice'
import TokenWarningModal from '../../components/TokenWarningModal'
import ProgressSteps from '../../components/ProgressSteps'

import { BETTER_TRADE_LINK_THRESHOLD, INITIAL_ALLOWED_SLIPPAGE } from '../../constants'
import { getTradeVersion, isTradeBetter } from '../../data/V1'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallbackFromTrade } from '../../hooks/useApproveCallback'
import useENSAddress from '../../hooks/useENSAddress'
import { useSwapCallback } from '../../hooks/useSwapCallback'
import useToggledVersion, { Version } from '../../hooks/useToggledVersion'
import useWrapCallback, { WrapType } from '../../hooks/useWrapCallback'
import { useToggleSettingsMenu, useWalletModalToggle } from '../../state/application/hooks'
import { Field } from '../../state/swap/actions'
import {
  useDefaultsFromURLSearch,
  useDerivedSwapInfo,
  useSwapActionHandlers,
  useSwapState
} from '../../state/swap/hooks'
import { useExpertModeManager, useUserDeadline, useUserSlippageTolerance } from '../../state/user/hooks'
import { LinkStyledButton, TYPE } from '../../theme'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from '../../utils/prices'
import AppBody from '../AppBody'
import { ClickableText } from '../Pool/styleds'
import Loader from '../../components/Loader'
import { useTokenTaxDetection } from '../../hooks/useTaxTokenDetection'
import TaxWarning from '../../components/swap/TaxWarning'
// 有税费代币检测
//import { isKnownTaxToken } from '../../utils/taxDetection'
import { useKline } from '../../state/kline/hooks'
import { findTokenPairByAddress, isTokenAllowed } from '../../services/tokenListApi'

export default function Swap() {
  const { t } = useTranslation()
  const loadedUrlParams = useDefaultsFromURLSearch()

  // token warning stuff
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.inputCurrencyId),
    useCurrency(loadedUrlParams?.outputCurrencyId)
  ]
  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c instanceof Token) ?? [],
    [loadedInputCurrency, loadedOutputCurrency]
  )
  const handleConfirmTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
  }, [])

  const { account } = useActiveWeb3React()
  const theme = useContext(ThemeContext)

  // toggle wallet when disconnected
  const toggleWalletModal = useWalletModalToggle()

  // for expert mode
  const toggleSettings = useToggleSettingsMenu()
  const [isExpertMode] = useExpertModeManager()

  // get custom setting values for user
  const [deadline] = useUserDeadline()
  const [allowedSlippage] = useUserSlippageTolerance()

  // swap state
  const { independentField, typedValue, recipient } = useSwapState()
  const {
    v1Trade,
    v2Trade,
    v2Trades,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError
  } = useDerivedSwapInfo()

  const inputCurrency = currencies[Field.INPUT]
  const outputCurrency = currencies[Field.OUTPUT]

  // 检测输入和输出代币的税费
  const inputTaxInfo = useTokenTaxDetection(inputCurrency instanceof Token ? inputCurrency : undefined)
  const outputTaxInfo = useTokenTaxDetection(outputCurrency instanceof Token ? outputCurrency : undefined)

  // 对路由进行排序，考虑税费因素
  const sortedTrades = useMemo(() => {
    if (!v2Trades || v2Trades.length === 0) return []

    // -------------------- 有税费代币检测 --------------------
    // // 检测所有代币的税费信息
    // const tokenTaxMap = new Map<string, boolean>()

    // // 检查每个路由中的代币
    // v2Trades.forEach(trade => {
    //   trade.route.path.forEach(token => {
    //     if (!tokenTaxMap.has(token.address)) {
    //       // 这里简化处理，只检查已知的税费代币
    //       const isTaxToken = isKnownTaxToken(token.chainId, token.address)
    //       tokenTaxMap.set(token.address, isTaxToken)
    //     }
    //   })
    // })

    // // 过滤路由：移除包含税费代币作为中间路由的路径
    // const filteredTrades = v2Trades.filter(trade => {
    //   // 如果路径长度小于等于2（直接兑换），保留
    //   if (trade.route.path.length <= 2) {
    //     return true
    //   }

    //   // 对于多跳路径，检查中间代币是否有税费
    //   // 跳过第一个和最后一个代币（输入和输出代币）
    //   for (let i = 1; i < trade.route.path.length - 1; i++) {
    //     const middleToken = trade.route.path[i]
    //     if (tokenTaxMap.get(middleToken.address)) {
    //       // 如果中间代币有税费，过滤掉这个路由
    //       return false
    //     }
    //   }

    //   return true
    // })

    // // 如果过滤后没有路由，保留原始路由（确保至少有一条路径）
    // const tradesToSort = filteredTrades.length > 0 ? filteredTrades : v2Trades
    // -------------------- 有税费代币检测 --------------------

    // 不再过滤有税费的代币，让所有代币都参与路由计算
    const tradesToSort = v2Trades

    return tradesToSort.sort((a, b) => {
      // 主排序：按"实际数量"选择最优路由
      // - exactIn：最大实际输出优先
      // - exactOut：最小实际输入优先

      // 计算实际输出（考虑输出税费）
      const getEffectiveOutputValue = (trade: Trade) => {
        let outputValue = parseFloat(trade.outputAmount.toSignificant(6))
        if (outputTaxInfo.hasTax) {
          const tax =
            independentField === Field.INPUT ? outputTaxInfo.buyTax || 0 : outputTaxInfo.sellTax || 0
          if (tax > 0) {
            outputValue = outputValue * (1 - tax / 100)
          }
        }
        return outputValue
      }

      // 计算实际输入（考虑输入税费）
      const getEffectiveInputValue = (trade: Trade) => {
        let inputValue = parseFloat(trade.inputAmount.toSignificant(6))
        if (inputTaxInfo.hasTax) {
          const tax =
            independentField === Field.INPUT ? inputTaxInfo.sellTax || 0 : inputTaxInfo.buyTax || 0
          if (tax > 0) {
            const keepPortion = 1 - tax / 100
            if (keepPortion > 0) {
              // 为达到同等效果需要投入的名义数量更高
              inputValue = inputValue / keepPortion
            }
          }
        }
        return inputValue
      }

      // 计算税费影响（税费百分比总和）
      const getTaxImpact = (trade: Trade) => {
        let totalTaxImpact = 0

        // 检查输入代币税费
        if (inputTaxInfo.hasTax) {
          if (independentField === Field.INPUT) {
            // 输入固定，考虑卖出税费
            totalTaxImpact += inputTaxInfo.sellTax || 0
          } else {
            // 输出固定，考虑买入税费
            totalTaxImpact += inputTaxInfo.buyTax || 0
          }
        }

        // 检查输出代币税费
        if (outputTaxInfo.hasTax) {
          if (independentField === Field.INPUT) {
            // 输入固定，考虑买入税费
            totalTaxImpact += outputTaxInfo.buyTax || 0
          } else {
            // 输出固定，考虑卖出税费
            totalTaxImpact += outputTaxInfo.sellTax || 0
          }
        }

        return totalTaxImpact
      }

      const isExactIn = independentField === Field.INPUT

      if (isExactIn) {
        const outA = getEffectiveOutputValue(a)
        const outB = getEffectiveOutputValue(b)
        if (outA !== outB) return outB - outA // 输出降序

        // 实际数量相同时，按税费影响最小排序
        const taxA = getTaxImpact(a)
        const taxB = getTaxImpact(b)
        if (taxA !== taxB) return taxA - taxB // 税费升序（越小越好）
      } else {
        const inA = getEffectiveInputValue(a)
        const inB = getEffectiveInputValue(b)
        if (inA !== inB) return inA - inB // 输入升序

        // 实际数量相同时，按税费影响最小排序
        const taxA = getTaxImpact(a)
        const taxB = getTaxImpact(b)
        if (taxA !== taxB) return taxA - taxB // 税费升序（越小越好）
      }

      // 三级排序：价格影响（更低更好）
      const priceImpactA = a.priceImpact
      const priceImpactB = b.priceImpact
      if (!priceImpactA.equalTo(priceImpactB)) {
        return priceImpactA.lessThan(priceImpactB) ? -1 : 1
      }

      // 末级排序：路径更短更好
      return a.route.path.length - b.route.path.length
    })
  }, [v2Trades, outputTaxInfo, inputTaxInfo, independentField])

  // 状态来跟踪选中的trade
  var [selectedTrade, setSelectedTrade] = useState<Trade | null>(null)

  // 当代币切换,重置selectedTrade状态
  useEffect(() => {
    setSelectedTrade(null)
  }, [inputCurrency, outputCurrency])

  // 移除当金额变化时重置selectedTrade的逻辑，保持当前路由
  // useEffect(() => {
  //   setSelectedTrade(null)
  // }, [typedValue])

  // 自定义的trade选择处理函数
  const handleTradeSelect = useCallback((trade: Trade) => {
    setSelectedTrade(trade)
  }, [])

  const { wrapType, execute: onWrap, inputError: wrapInputError, isLoading: wrapIsLoading } = useWrapCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const { address: recipientAddress } = useENSAddress(recipient)
  const toggledVersion = useToggledVersion()
  // 使用选中的路径，如果没有选中则使用考虑税费后的最优路径
  const currentTrade = useMemo(() => {
    // 如果用户手动选择了路由，使用选中的路由
    if (selectedTrade) {
      return selectedTrade
    }
    // 否则使用考虑税费后的最优路由
    return sortedTrades.length > 0 ? sortedTrades[0] : v2Trade
  }, [selectedTrade, sortedTrades, v2Trade])

  const trade = showWrap
    ? undefined
    : {
        [Version.v1]: v1Trade,
        [Version.v2]: currentTrade
      }[toggledVersion]

  const betterTradeLinkVersion: Version | undefined =
    toggledVersion === Version.v2 && isTradeBetter(v2Trade, v1Trade, BETTER_TRADE_LINK_THRESHOLD)
      ? Version.v1
      : toggledVersion === Version.v1 && isTradeBetter(v1Trade, v2Trade)
      ? Version.v2
      : undefined

  // 计算parsedAmounts，当用户选择了特定路由时重新计算
  const parsedAmounts = useMemo(() => {
    if (showWrap) {
      return {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: parsedAmount
      }
    }

    // 如果用户选择了特定路由且当前是输入字段，重新计算输出金额
    if (selectedTrade && independentField === Field.INPUT && parsedAmount) {
      try {
        // 基于选中的路由和新的输入金额重新计算输出金额
        const newTrade = Trade.exactIn(selectedTrade.route, parsedAmount, selectedTrade.originalOutputCurrency)
        return {
          [Field.INPUT]: parsedAmount,
          [Field.OUTPUT]: newTrade.outputAmount
        }
      } catch (error) {
        // 如果重新计算失败，回退到原始逻辑
        console.warn('Failed to recalculate trade with selected route:', error)
      }
    }

    // 如果用户选择了特定路由且当前是输出字段，重新计算输入金额
    if (selectedTrade && independentField === Field.OUTPUT && parsedAmount) {
      try {
        // 基于选中的路由和新的输出金额重新计算输入金额
        const newTrade = Trade.exactOut(selectedTrade.route, parsedAmount, selectedTrade.originalOutputCurrency)
        return {
          [Field.INPUT]: newTrade.inputAmount,
          [Field.OUTPUT]: parsedAmount
        }
      } catch (error) {
        // 如果重新计算失败，回退到原始逻辑
        console.warn('Failed to recalculate trade with selected route:', error)
      }
    }

    // 默认逻辑 - 使用currentTrade而不是trade来避免循环依赖
    return {
      [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : currentTrade?.inputAmount,
      [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : currentTrade?.outputAmount
    }
  }, [showWrap, selectedTrade, independentField, parsedAmount, currentTrade])

  // 创建最终的trade对象，当用户选择了特定路由且修改了金额时使用重新计算的金额
  const finalTrade = useMemo(() => {
    if (showWrap) {
      return undefined
    }

    // 如果用户选择了特定路由且重新计算了金额，创建新的trade
    if (selectedTrade && parsedAmount) {
      try {
        if (independentField === Field.INPUT) {
          const newTrade = Trade.exactIn(selectedTrade.route, parsedAmount, selectedTrade.originalOutputCurrency)
          // 只有当新trade与当前trade不同时才返回新trade
          if (!currentTrade || newTrade.outputAmount.toSignificant(6) !== currentTrade.outputAmount.toSignificant(6)) {
            return newTrade
          }
        } else if (independentField === Field.OUTPUT) {
          const newTrade = Trade.exactOut(selectedTrade.route, parsedAmount, selectedTrade.originalOutputCurrency)
          // 只有当新trade与当前trade不同时才返回新trade
          if (!currentTrade || newTrade.inputAmount.toSignificant(6) !== currentTrade.inputAmount.toSignificant(6)) {
            return newTrade
          }
        }
      } catch (error) {
        console.warn('Failed to create new trade with selected route:', error)
      }
    }

    // 否则使用currentTrade
    return currentTrade
  }, [showWrap, selectedTrade, parsedAmount, independentField, currentTrade])

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
  const isValid = !swapInputError
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      // 如果输入代币是税费代币，不允许使用 EXACT_OUTPUT 模式
      if (inputTaxInfo.hasTax && (inputTaxInfo.sellTax || 0) > 0) {
        return // 不执行任何操作，阻止用户输入
      }
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput, inputTaxInfo]
  )

  // modal and loading
  const [{ showConfirm, tradeToConfirm, swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
    showConfirm: boolean
    tradeToConfirm: Trade | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined
  })

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: showWrap
      ? parsedAmounts[independentField]?.toExact() ?? ''
      : parsedAmounts[dependentField]?.toSignificant(6) ?? ''
  }

  const route = finalTrade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )
  const noRoute = !route

  // check whether the user has approved the router on the input token
  const [approval, approveCallback, approvalLoading] = useApproveCallbackFromTrade(finalTrade, allowedSlippage)

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval])

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
  const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))

  // the callback to execute the swap
  const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(
    finalTrade,
    allowedSlippage,
    deadline,
    recipient
  )

  const { priceImpactWithoutFee } = computeTradePriceBreakdown(finalTrade)

  const handleSwap = useCallback(() => {
    if (priceImpactWithoutFee && !confirmPriceImpactWithoutFee(priceImpactWithoutFee)) {
      return
    }
    if (!swapCallback) {
      return
    }
    setSwapState({ attemptingTxn: true, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: undefined })
    swapCallback()
      .then(hash => {
        setSwapState({ attemptingTxn: false, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: hash })

        ReactGA.event({
          category: 'Swap',
          action:
            recipient === null
              ? 'Swap w/o Send'
              : (recipientAddress ?? recipient) === account
              ? 'Swap w/o Send + recipient'
              : 'Swap w/ Send',
          label: [
            finalTrade?.inputAmount?.currency?.symbol,
            finalTrade?.outputAmount?.currency?.symbol,
            getTradeVersion(finalTrade)
          ].join('/')
        })
      })
      .catch(error => {
        setSwapState({
          attemptingTxn: false,
          tradeToConfirm,
          showConfirm,
          swapErrorMessage: error.message,
          txHash: undefined
        })
      })
  }, [
    tradeToConfirm,
    account,
    priceImpactWithoutFee,
    recipient,
    recipientAddress,
    showConfirm,
    swapCallback,
    finalTrade
  ])

  // errors
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // warnings on slippage
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  // never show if price impact is above threshold in non expert mode
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3 && !isExpertMode)

  const handleConfirmDismiss = useCallback(() => {
    setSwapState({ showConfirm: false, tradeToConfirm, attemptingTxn, swapErrorMessage, txHash })
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxn, onUserInput, swapErrorMessage, tradeToConfirm, txHash])

  const handleAcceptChanges = useCallback(() => {
    setSwapState({ tradeToConfirm: trade, swapErrorMessage, txHash, attemptingTxn, showConfirm })
  }, [attemptingTxn, showConfirm, swapErrorMessage, trade, txHash])

  const { setCurrentPair, clearPair } = useKline()

  const handleInputSelect = useCallback(
    inputCurrency => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      onCurrencySelection(Field.INPUT, inputCurrency)

      // 只有在允许列表中的币种被选中时才更新setCurrentTradingPair
      if (isTokenAllowed(inputCurrency.address)) {
        const tokenPair = findTokenPairByAddress(inputCurrency.address)
        console.log('Token allowed, updating trading pair:', inputCurrency.symbol)
        setCurrentPair({
          token0: {
            chainId: inputCurrency.chainId,
            address: inputCurrency.address,
            decimals: inputCurrency.decimals,
            symbol: inputCurrency.symbol,
            name: inputCurrency.name,
            logo: inputCurrency.logoURI,
            key: tokenPair?.key
          },
          token1: {
            chainId: inputCurrency.chainId,
            address: inputCurrency.address,
            decimals: inputCurrency.decimals,
            symbol: inputCurrency.symbol,
            name: inputCurrency.name,
            logo: inputCurrency.logoURI,
            key: tokenPair?.key
          },
          pairAddress: undefined,
          reserve0: undefined,
          reserve1: undefined,
          price: undefined,
          priceChange24h: undefined,
          volume24h: undefined
        })
      } else {
        clearPair()
        console.log('Token not in allowed list, skipping trading pair update:', inputCurrency.symbol)
      }
    },
    [onCurrencySelection, setCurrentPair, clearPair]
  )

  const handleMaxInput = useCallback(() => {
    maxAmountInput && onUserInput(Field.INPUT, maxAmountInput.toExact())
  }, [maxAmountInput, onUserInput])

  const handleOutputSelect = useCallback(
    outputCurrency => {
      onCurrencySelection(Field.OUTPUT, outputCurrency)
    },
    [onCurrencySelection]
  )

  return (
    <>
      <TokenWarningModal
        isOpen={urlLoadedTokens.length > 0 && !dismissTokenWarning}
        tokens={urlLoadedTokens}
        onConfirm={handleConfirmTokenWarning}
      />
      <AppBody>
        <SwapPoolTabs active={'swap'} />
        <Wrapper id="swap-page">
          <ConfirmSwapModal
            isOpen={showConfirm}
            trade={trade}
            originalTrade={tradeToConfirm}
            onAcceptChanges={handleAcceptChanges}
            attemptingTxn={attemptingTxn}
            txHash={txHash}
            recipient={recipient}
            allowedSlippage={allowedSlippage}
            onConfirm={handleSwap}
            swapErrorMessage={swapErrorMessage}
            onDismiss={handleConfirmDismiss}
          />

          <AutoColumn gap={'md'}>
            <CurrencyInputPanel
              label={independentField === Field.OUTPUT && !showWrap && trade ? t('From (estimated)') : t('From')}
              value={formattedAmounts[Field.INPUT]}
              showMaxButton={!atMaxAmountInput}
              currency={currencies[Field.INPUT]}
              onUserInput={handleTypeInput}
              onMax={handleMaxInput}
              onCurrencySelect={handleInputSelect}
              otherCurrency={currencies[Field.OUTPUT]}
              id="swap-currency-input"
            />
            <AutoColumn justify="space-between">
              <AutoRow justify={isExpertMode ? 'space-between' : 'center'} style={{ padding: '0 1rem' }}>
                <ArrowWrapper clickable>
                  <Repeat
                    style={{ transform: 'rotate(90deg)' }}
                    size="16"
                    onClick={() => {
                      setApprovalSubmitted(false) // reset 2 step UI for approvals
                      onSwitchTokens()
                    }}
                    color={currencies[Field.INPUT] && currencies[Field.OUTPUT] ? theme.primary1 : theme.text2}
                  />
                </ArrowWrapper>
                {recipient === null && !showWrap && isExpertMode ? (
                  <LinkStyledButton id="add-recipient-button" onClick={() => onChangeRecipient('')}>
                    + {t('Add a send (optional)')}
                  </LinkStyledButton>
                ) : null}
              </AutoRow>
            </AutoColumn>
            <CurrencyInputPanel
              value={formattedAmounts[Field.OUTPUT]}
              onUserInput={handleTypeOutput}
              label={independentField === Field.INPUT && !showWrap && trade ? t('To (estimated)') : t('To')}
              showMaxButton={false}
              currency={currencies[Field.OUTPUT]}
              onCurrencySelect={handleOutputSelect}
              otherCurrency={currencies[Field.INPUT]}
              id="swap-currency-output"
              disabled={inputTaxInfo.hasTax && (inputTaxInfo.sellTax || 0) > 0}
            />

            {recipient !== null && !showWrap ? (
              <>
                <AutoRow justify="space-between" style={{ padding: '0 1rem' }}>
                  <ArrowWrapper clickable={false}>
                    <ArrowDown size="16" color={theme.text2} />
                  </ArrowWrapper>
                  <LinkStyledButton id="remove-recipient-button" onClick={() => onChangeRecipient(null)}>
                    - {t('Remove send')}
                  </LinkStyledButton>
                </AutoRow>
                <AddressInputPanel id="recipient" value={recipient} onChange={onChangeRecipient} />
              </>
            ) : null}

            {showWrap ? null : (
              <>
                <Card padding={'.25rem .75rem 0 .75rem'} borderRadius={'20px'}>
                  <AutoColumn gap="4px">
                    {Boolean(trade) && (
                      <RowBetween align="center">
                        <Text fontWeight={500} fontSize={14} color={theme.text2}>
                          {t('Price')}
                        </Text>
                        <TradePrice
                          price={trade?.executionPrice}
                          showInverted={showInverted}
                          setShowInverted={setShowInverted}
                        />
                      </RowBetween>
                    )}
                    {/* 税费代币提示 */}
                    <TaxWarning
                      inputTaxInfo={inputTaxInfo}
                      outputTaxInfo={outputTaxInfo}
                      outputToken={outputCurrency instanceof Token ? outputCurrency : undefined}
                    />
                    {allowedSlippage !== INITIAL_ALLOWED_SLIPPAGE && (
                      <RowBetween align="center">
                        <ClickableText fontWeight={500} fontSize={14} color={theme.text2} onClick={toggleSettings}>
                          {t('Slippage Tolerance')}
                        </ClickableText>
                        <ClickableText fontWeight={500} fontSize={14} color={theme.text2} onClick={toggleSettings}>
                          {allowedSlippage / 100}%
                        </ClickableText>
                      </RowBetween>
                    )}
                  </AutoColumn>
                </Card>
              </>
            )}
          </AutoColumn>
          <BottomGrouping>
            {!account ? (
              <ButtonLight onClick={toggleWalletModal}>{t('Connect Wallet')}</ButtonLight>
            ) : showWrap ? (
              <ButtonPrimary disabled={Boolean(wrapInputError) || wrapIsLoading} onClick={onWrap}>
                {wrapIsLoading ? (
                  <AutoRow gap="6px" justify="center">
                    {wrapType === WrapType.WRAP ? t('wrapping') : t('unwrapping')} <Loader stroke="white" />
                  </AutoRow>
                ) : (
                  wrapInputError ??
                  (wrapType === WrapType.WRAP ? t('Wrap') : wrapType === WrapType.UNWRAP ? t('Unwrap') : null)
                )}
              </ButtonPrimary>
            ) : noRoute && userHasSpecifiedInputOutput ? (
              <GreyCard style={{ textAlign: 'center' }}>
                <TYPE.main mb="4px">{t('Insufficient liquidity for this trade.')}</TYPE.main>
              </GreyCard>
            ) : showApproveFlow ? (
              <RowBetween>
                <ButtonConfirmed
                  onClick={approveCallback}
                  disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted || approvalLoading}
                  width="48%"
                  altDisabledStyle={approval === ApprovalState.PENDING || approvalLoading} // show solid button while waiting
                  confirmed={approval === ApprovalState.APPROVED}
                >
                  {approval === ApprovalState.PENDING || approvalLoading ? (
                    <AutoRow gap="6px" justify="center">
                      {t('Approving')} <Loader stroke="white" />
                    </AutoRow>
                  ) : approvalSubmitted && approval === ApprovalState.APPROVED ? (
                    t('Approved')
                  ) : (
                    t('Approve') + ' ' + currencies[Field.INPUT]?.symbol
                  )}
                </ButtonConfirmed>
                <ButtonError
                  onClick={() => {
                    if (isExpertMode) {
                      handleSwap()
                    } else {
                      setSwapState({
                        tradeToConfirm: finalTrade,
                        attemptingTxn: false,
                        swapErrorMessage: undefined,
                        showConfirm: true,
                        txHash: undefined
                      })
                    }
                  }}
                  width="48%"
                  id="swap-button"
                  disabled={
                    !isValid || approval !== ApprovalState.APPROVED || (priceImpactSeverity > 3 && !isExpertMode)
                  }
                  error={isValid && priceImpactSeverity > 2}
                >
                  <Text fontSize={16} fontWeight={500}>
                    {priceImpactSeverity > 3 && !isExpertMode
                      ? t('Price Impact High')
                      : t('swap') + (priceImpactSeverity > 2 ? ' ' + t('Swap Anyway') : '')}
                  </Text>
                </ButtonError>
              </RowBetween>
            ) : (
              <ButtonError
                onClick={() => {
                  if (isExpertMode) {
                    handleSwap()
                  } else {
                    setSwapState({
                      tradeToConfirm: finalTrade,
                      attemptingTxn: false,
                      swapErrorMessage: undefined,
                      showConfirm: true,
                      txHash: undefined
                    })
                  }
                }}
                id="swap-button"
                disabled={!isValid || (priceImpactSeverity > 3 && !isExpertMode) || !!swapCallbackError}
                error={isValid && priceImpactSeverity > 2 && !swapCallbackError}
              >
                <Text fontSize={20} fontWeight={500}>
                  {swapInputError
                    ? swapInputError
                    : priceImpactSeverity > 3 && !isExpertMode
                    ? t('Price Impact Too High')
                    : t('swap') + (priceImpactSeverity > 2 ? ' ' + t('Swap Anyway') : '')}
                </Text>
              </ButtonError>
            )}
            {showApproveFlow && <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />}
            {isExpertMode && swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
            {betterTradeLinkVersion && <BetterTradeLink version={betterTradeLinkVersion} />}
          </BottomGrouping>
        </Wrapper>
      </AppBody>
      <AdvancedSwapDetailsDropdown
        trade={finalTrade}
        trades={sortedTrades}
        selectedTrade={selectedTrade}
        onTradeSelect={handleTradeSelect}
      />
    </>
  )
}
